import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useAddEquipmentPart,
  useAssets,
  useDeleteEquipment,
  useDeleteEquipmentPart,
  useEquipment,
  useLogs,
  usePatchEquipmentPart,
  useZones,
} from '@/lib/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import EquipmentForm from '@/components/EquipmentForm';
import LogList from '@/components/LogList';
import PhotoGrid from '@/components/PhotoGrid';
import { useAppShell } from '@/components/AppShellContext';

export default function EquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const q = useEquipment(id);
  const zonesQ = useZones();
  const logs = useLogs({ entityType: 'equipment', entityId: id, limit: 50 });
  const photos = useAssets({ entityType: 'equipment', entityId: id, limit: 60 });
  const del = useDeleteEquipment();
  const navigate = useNavigate();
  const { openLogForm, openPhotoSheet } = useAppShell();
  const [editing, setEditing] = useState(false);

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;
  const { equipment, parts } = q.data;
  const storageZone = (zonesQ.data ?? []).find((z) => z.id === equipment.storageZoneId);

  async function onDelete() {
    if (!confirm('Delete this equipment record? This cannot be undone (parts go with it).')) return;
    await del.mutateAsync(equipment.id);
    navigate('/equipment');
  }

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <Link to="/equipment" className="hover:text-foreground">
            Equipment
          </Link>{' '}
          <span aria-hidden>·</span> {equipment.category}
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          {equipment.name}
        </h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {[equipment.brand, equipment.model].filter(Boolean).length > 0 ? (
            <span className="font-mono uppercase tracking-wider">
              {[equipment.brand, equipment.model].filter(Boolean).join(' · ')}
            </span>
          ) : null}
          {equipment.serialNumber ? (
            <span className="font-mono">SN {equipment.serialNumber}</span>
          ) : null}
          {storageZone ? (
            <span className="font-mono uppercase tracking-wider">{storageZone.name}</span>
          ) : null}
          <span className="font-mono uppercase tracking-wider">{equipment.status}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setEditing(true)} data-testid="eq-edit">
            <Pencil className="h-4 w-4 mr-2" aria-hidden /> Edit
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              openLogForm({
                domainId: 'dom_seed_equipment',
                entityType: 'equipment',
                entityId: equipment.id,
                title: `${equipment.name} — `,
              })
            }
          >
            Log work
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              openPhotoSheet({
                domainId: 'dom_seed_equipment',
                entityType: 'equipment',
                entityId: equipment.id,
                tags: ['equipment'],
              })
            }
          >
            Add photo
          </Button>
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4 mr-2" aria-hidden /> Delete
          </Button>
        </div>
      </header>

      <Tabs defaultValue="parts">
        <TabsList className="overflow-x-auto -mx-1 px-1 max-w-full whitespace-nowrap">
          <TabsTrigger value="parts" data-testid="eq-tab-parts">
            Parts ({parts.length})
          </TabsTrigger>
          <TabsTrigger value="logs">Logs ({logs.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="photos">Photos ({photos.data?.length ?? 0})</TabsTrigger>
          {equipment.notes ? <TabsTrigger value="notes">Notes</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="parts">
          <PartsTable equipmentId={equipment.id} parts={parts} />
        </TabsContent>
        <TabsContent value="logs">
          <LogList logs={logs.data ?? []} emptyMessage="No logs for this equipment yet." />
        </TabsContent>
        <TabsContent value="photos">
          <PhotoGrid assets={photos.data ?? []} emptyMessage="No photos for this equipment yet." />
        </TabsContent>
        {equipment.notes ? (
          <TabsContent value="notes">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-base">Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 whitespace-pre-wrap leading-relaxed">
                {equipment.notes}
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}
      </Tabs>

      <EquipmentForm open={editing} onOpenChange={setEditing} mode="edit" equipment={equipment} />
    </div>
  );
}

function PartsTable({
  equipmentId,
  parts,
}: {
  equipmentId: string;
  parts: { id: string; partName: string; partNumber: string | null; notes: string | null }[];
}) {
  const add = useAddEquipmentPart(equipmentId);
  const patch = usePatchEquipmentPart(equipmentId);
  const del = useDeleteEquipmentPart(equipmentId);
  const [adding, setAdding] = useState(false);
  const [partName, setPartName] = useState('');
  const [partNumber, setPartNumber] = useState('');

  async function onAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!partName.trim()) return;
    await add.mutateAsync({ partName: partName.trim(), partNumber: partNumber.trim() || null });
    setPartName('');
    setPartNumber('');
    setAdding(false);
  }

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="parts-list">
        {parts.length === 0 ? (
          <li className="px-4 py-3 text-sm text-muted-foreground">
            No parts recorded yet.
          </li>
        ) : (
          parts.map((p) => (
            <li key={p.id} className="flex items-start gap-3 px-4 py-3" data-testid={`part-${p.id}`}>
              <div className="flex-1 min-w-0 space-y-0.5">
                <p className="font-display text-base font-medium leading-tight">{p.partName}</p>
                {p.partNumber ? (
                  <p className="font-mono text-xs text-muted-foreground" data-testid={`part-${p.id}-number`}>
                    PN {p.partNumber}
                  </p>
                ) : null}
                {p.notes ? <p className="text-sm text-muted-foreground">{p.notes}</p> : null}
              </div>
              <PartNumberEditor partId={p.id} initial={p.partNumber} onSave={(num) => patch.mutateAsync({ partId: p.id, patch: { partNumber: num } })} />
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={() => del.mutateAsync(p.id)}
                aria-label="Delete part"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            </li>
          ))
        )}
      </ul>

      {adding ? (
        <form onSubmit={onAdd} className="flex flex-col gap-2 sm:flex-row sm:items-center" data-testid="part-form">
          <Input
            placeholder="Part name (e.g. Air filter)"
            value={partName}
            onChange={(e) => setPartName(e.target.value)}
            className="h-11 sm:flex-1"
            data-testid="part-name"
            autoFocus
            required
          />
          <Input
            placeholder="Part number (optional)"
            value={partNumber}
            onChange={(e) => setPartNumber(e.target.value)}
            className="h-11 sm:flex-1"
            data-testid="part-number"
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={add.isPending} data-testid="part-submit">
              {add.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" onClick={() => setAdding(true)} data-testid="add-part">
          <Plus className="h-4 w-4 mr-2" aria-hidden /> Add part
        </Button>
      )}
    </div>
  );
}

function PartNumberEditor({
  partId,
  initial,
  onSave,
}: {
  partId: string;
  initial: string | null;
  onSave: (next: string | null) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial ?? '');
  if (!editing) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setValue(initial ?? '');
          setEditing(true);
        }}
        className="h-9 font-mono text-[11px] uppercase tracking-wider"
        data-testid={`edit-part-${partId}`}
      >
        Edit PN
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
        onBlur={() => {
          const next = value.trim();
          void onSave(next === '' ? null : next).then(() => setEditing(false));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const next = value.trim();
            void onSave(next === '' ? null : next).then(() => setEditing(false));
          }
          if (e.key === 'Escape') setEditing(false);
        }}
        className="h-9 w-32 font-mono text-xs"
        data-testid={`part-${partId}-input`}
      />
    </div>
  );
}
